import React, { useMemo, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  PageContainer,
  Container,
  PageTitle,
  PageSubtitle,
  Section,
  ContentText,
  FeatureCard,
  CardTitle,
  CardDescription,
  GridContainer,
  ImageTextSection,
  ImageTextImage,
  ImageTextContent,
  SolutionsHeroSection,
  HeroContent,
  HeroTitle,
  HeroSubtitle,
  HeroContentWrapper,
  PartnershipSection,
  PartnershipContent,
  SolutionsCtaTitle,
  SolutionsCtaText,
  SolutionsCtaButton,
  SeparatorImage,
  staggerContainer,
  staggerItem,
  heroStaggerContainerVariants,
  heroStaggerItemVariants,
  TextBulletCardsGrid,
  LazyImage,
  CtaImageSection,
  LazyBackgroundImage,
  SolutionsSectionHeading,
  SolutionBulletCardLight,
  BulletList,
  SectionDivider,
} from '../../components/shared/SharedComponents';
import { useContent } from '../../hooks/useContent';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import SEO from '../../components/SEO';
import {
  preloadCriticalImages,
  preloadImagesInBackground,
  getSubsectionImageUrls,
} from '../../utils/imagePreloader';
import { BACKGROUND_PRELOAD_MAX_SIBLINGS } from '../../config/preloadConfig';
import { normalizeBullet } from '../../utils/contentUtils';

const ContentSectionWrapper = styled.div`
  margin-bottom: 60px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionSubtitle = styled.p`
  font-size: clamp(16px, 2vw, 18px);
  font-weight: 700;
  color: rgba(29, 51, 78, 0.85);
  margin: -4px 0 16px 0;
  line-height: 1.45;
`;

const SubsectionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 32px;
  margin-bottom: 16px;
  
  &:first-child {
    margin-top: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const SubsectionHeading = styled.h4`
  font-size: clamp(20px, 3vw, 20px);
  font-weight: 600;
  color: #1d334e;
  margin: 0;
  line-height: 1.4;
  flex: 1;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SubsectionImage = styled(motion.div)`
  flex-shrink: 0;
  width: 120px;
  height: 120px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.1);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  
  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const BulletPointCard = motion(SolutionBulletCardLight);

const BulletPointCardTitle = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #1d334e;
  margin-bottom: 10px;
  line-height: 1.3;
`;

const BulletPointCardText = styled.p`
  font-size: 16px;
  color: rgba(29, 51, 78, 0.8);
  line-height: 1.5;
  margin: 0;
  
  strong {
    color: #1d334e;
    font-weight: 600;
  }
`;

const BulletPointCardList = styled(BulletList)`
  margin: 0;

  li {
    margin-bottom: 6px;
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

// Bullet cards grid limited to 3 columns on desktop
const BulletCardsGrid = styled(TextBulletCardsGrid)`
  grid-template-columns: repeat(3, 1fr);
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

/** Full-width bullet stack inside two-column subsection cells (avoids 1/3-width single cards) */
const BulletCardsGridColumn = styled(BulletCardsGrid)`
  grid-template-columns: 1fr;
`;

const imageAnimation = {
  initial: { opacity: 0, scale: 0.96 },
  whileInView: { opacity: 1, scale: 1 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
};

const SubsectionBlock = ({ subsec, layout, renderBulletCards }) => {
  const isCompact = layout === 'compact';
  const SubsecWrapper = ({ children }) => <div style={{ marginTop: '32px' }}>{children}</div>;

  if (subsec.type === 'divider') {
    return (
      <SubsecWrapper>
        <SectionDivider />
      </SubsecWrapper>
    );
  }

  if (subsec.type === 'twoColumns' && (subsec.left || subsec.right)) {
    const renderColumn = (col, side) => {
      if (!col) return null;
      const hasHeading = Boolean(col.title || col.paragraphs?.length);
      return (
        <div key={side}>
          {col.title && <SolutionsSectionHeading>{col.title}</SolutionsSectionHeading>}
          {col.paragraphs && (
            <ContentText>
              {col.paragraphs.map((paragraph, pIndex) => (
                <p key={`${side}-p-${pIndex}`}>{paragraph}</p>
              ))}
            </ContentText>
          )}
          {col.bullets && (
            <div style={{ marginTop: hasHeading ? '16px' : 0 }}>
              {renderBulletCards(col.bullets, { grid: BulletCardsGridColumn })}
            </div>
          )}
        </div>
      );
    };
    return (
      <SubsecWrapper>
        <GridContainer className="two-columns" as={motion.div} {...imageAnimation}>
          {renderColumn(subsec.left, 'left')}
          {renderColumn(subsec.right, 'right')}
        </GridContainer>
      </SubsecWrapper>
    );
  }

  if (subsec.type === 'imageDivider' && (subsec.image || subsec.src)) {
    const imageSrc = subsec.image || subsec.src;
    return (
      <SubsecWrapper>
        <LazyBackgroundImage src={imageSrc} critical>
          <SeparatorImage
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          />
        </LazyBackgroundImage>
      </SubsecWrapper>
    );
  }

  if (isCompact) {
    return (
      <SubsecWrapper>
        <SubsectionContainer>
          {subsec.image && (
            <SubsectionImage as={motion.div} {...imageAnimation}>
              <LazyImage
                src={subsec.image}
                alt={subsec.title || 'Element visual'}
                height="120px"
                objectFit="cover"
                critical
              />
            </SubsectionImage>
          )}
          {subsec.title && <SubsectionHeading>{subsec.title}</SubsectionHeading>}
        </SubsectionContainer>
        {subsec.boldParagraphs?.map((text, i) => (
          <SectionSubtitle key={`bold-${i}`}>{text}</SectionSubtitle>
        ))}
        {subsec.paragraphs && (
          <ContentText>
            {subsec.paragraphs.map((paragraph, pIndex) => (
              <p key={`p-${pIndex}`}>{paragraph}</p>
            ))}
          </ContentText>
        )}
        {subsec.bullets && renderBulletCards(subsec.bullets)}
        {subsec.paragraphsAfter && (
          <ContentText>
            {subsec.paragraphsAfter.map((paragraph, pIndex) => (
              <p key={`p-after-${pIndex}`}>{paragraph}</p>
            ))}
          </ContentText>
        )}
      </SubsecWrapper>
    );
  }

  if (subsec.image) {
    const pos = (subsec.imagePosition || 'left').toString().trim().toLowerCase();
    const imageOnLeft = pos !== 'right';
    const imageBlock = (
      <ImageTextImage as={motion.div} {...imageAnimation}>
        <LazyImage
          src={subsec.image}
          alt={subsec.title || 'Section visual'}
          height="100%"
          objectFit="cover"
          critical
        />
      </ImageTextImage>
    );
    const contentBlock = (
      <ImageTextContent>
        {subsec.title && <SolutionsSectionHeading>{subsec.title}</SolutionsSectionHeading>}
        {subsec.boldParagraphs?.map((text, i) => (
          <SectionSubtitle key={`bold-${i}`}>{text}</SectionSubtitle>
        ))}
        {subsec.paragraphs && (
          <ContentText>
            {subsec.paragraphs.map((paragraph, pIndex) => (
              <p key={`p-${pIndex}`}>{paragraph}</p>
            ))}
          </ContentText>
        )}
      </ImageTextContent>
    );
    return (
      <SubsecWrapper>
        <ImageTextSection>
          {imageOnLeft ? (
            <>
              {imageBlock}
              {contentBlock}
            </>
          ) : (
            <>
              {contentBlock}
              {imageBlock}
            </>
          )}
        </ImageTextSection>
        {subsec.bullets && (
          <div style={{ marginTop: '24px' }}>{renderBulletCards(subsec.bullets)}</div>
        )}
        {subsec.paragraphsAfter && (
          <ContentText style={{ marginTop: subsec.bullets ? '24px' : '16px' }}>
            {subsec.paragraphsAfter.map((paragraph, pIndex) => (
              <p key={`p-after-${pIndex}`}>{paragraph}</p>
            ))}
          </ContentText>
        )}
      </SubsecWrapper>
    );
  }

  return (
    <SubsecWrapper>
      {subsec.title && <SolutionsSectionHeading>{subsec.title}</SolutionsSectionHeading>}
      {subsec.boldParagraphs?.map((text, i) => (
        <SectionSubtitle key={`bold-${i}`}>{text}</SectionSubtitle>
      ))}
      {subsec.paragraphs && (
        <ContentText>
          {subsec.paragraphs.map((paragraph, pIndex) => (
            <p key={`p-${pIndex}`}>{paragraph}</p>
          ))}
        </ContentText>
      )}
      {subsec.bullets && renderBulletCards(subsec.bullets)}
      {subsec.paragraphsAfter && (
        <ContentText>
          {subsec.paragraphsAfter.map((paragraph, pIndex) => (
            <p key={`p-after-${pIndex}`}>{paragraph}</p>
          ))}
        </ContentText>
      )}
    </SubsecWrapper>
  );
};

function renderSectionContent(section, sectionIndex, { renderBulletCards }) {
  const sectionType = section.type || 'default';
  const compactSubsections = section.subsectionsLayout === 'compact';
  const fullWidthSectionImage = section.sectionImageLayout === 'fullWidth';

  if (sectionType === 'divider') {
    return (
      <ContentSectionWrapper
        key={`section-${sectionIndex}`}
        as={motion.div}
        variants={staggerItem}
      >
        <SectionDivider />
      </ContentSectionWrapper>
    );
  }

  if (sectionType === 'image' && (section.image || section.src)) {
    const imageSrc = section.image || section.src;
    return (
      <ContentSectionWrapper
        key={`section-${sectionIndex}`}
        as={motion.div}
        variants={staggerItem}
      >
        <LazyBackgroundImage src={imageSrc} critical>
          <SeparatorImage
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          />
        </LazyBackgroundImage>
      </ContentSectionWrapper>
    );
  }

  if (sectionType === 'intro') {
    return (
      <ContentSectionWrapper
        key={`section-${sectionIndex}`}
        as={motion.div}
        variants={staggerItem}
      >
        {section.title && <SolutionsSectionHeading>{section.title}</SolutionsSectionHeading>}
        {section.boldParagraphs?.map((text, i) => (
          <SectionSubtitle key={`bold-${i}`}>{text}</SectionSubtitle>
        ))}
        <ContentText>
          {section.paragraphs?.map((paragraph, pIndex) => (
            <p key={`p-${pIndex}`}>{paragraph}</p>
          ))}
        </ContentText>
      </ContentSectionWrapper>
    );
  }

  if (sectionType === 'heading') {
    const imageSrc = section.image || section.src;
    /** Full-width title, then two columns: image + body (bullets/card on the right) */
    const titleThenImageText =
      section.sectionImageLayout === 'titleThenColumns' && imageSrc && section.title;
    const showFullWidthImage = imageSrc && fullWidthSectionImage;
    const showTwoColumnImage =
      imageSrc && section.title && !fullWidthSectionImage && !titleThenImageText;

    return (
      <ContentSectionWrapper
        key={`section-${sectionIndex}`}
        as={motion.div}
        variants={staggerItem}
      >
        {showFullWidthImage && (
          <LazyBackgroundImage src={imageSrc} style={{ marginBottom: '32px' }} critical>
            <SeparatorImage
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            />
          </LazyBackgroundImage>
        )}
        {showTwoColumnImage && (() => {
          const pos = (section.imagePosition || 'left').toString().trim().toLowerCase();
          const imageOnLeft = pos !== 'right';
          const imageBlock = (
            <ImageTextImage as={motion.div} {...imageAnimation}>
              <LazyImage
                src={imageSrc}
                alt={section.title}
                height="100%"
                objectFit="cover"
                critical
              />
            </ImageTextImage>
          );
          const contentBlock = (
            <ImageTextContent>
              <SolutionsSectionHeading>{section.title}</SolutionsSectionHeading>
              {section.boldParagraphs?.map((text, i) => (
                <SectionSubtitle key={`bold-${i}`}>{text}</SectionSubtitle>
              ))}
              {section.paragraphs && (
                <ContentText>
                  {section.paragraphs.map((paragraph, pIndex) => (
                    <p key={`p-${pIndex}`}>{paragraph}</p>
                  ))}
                </ContentText>
              )}
            </ImageTextContent>
          );
          return (
            <ImageTextSection style={{ marginTop: 0 }}>
              {imageOnLeft ? (
                <>
                  {imageBlock}
                  {contentBlock}
                </>
              ) : (
                <>
                  {contentBlock}
                  {imageBlock}
                </>
              )}
            </ImageTextSection>
          );
        })()}
        {titleThenImageText && (() => {
          const pos = (section.imagePosition || 'left').toString().trim().toLowerCase();
          const imageOnLeft = pos !== 'right';
          const imageBlock = (
            <ImageTextImage as={motion.div} {...imageAnimation}>
              <LazyImage
                src={imageSrc}
                alt={section.title}
                height="100%"
                objectFit="cover"
                critical
              />
            </ImageTextImage>
          );
          const contentBlock = (
            <ImageTextContent>
              {section.boldParagraphs?.map((text, i) => (
                <SectionSubtitle key={`bold-${i}`}>{text}</SectionSubtitle>
              ))}
              {section.paragraphs && (
                <ContentText>
                  {section.paragraphs.map((paragraph, pIndex) => (
                    <p key={`p-${pIndex}`}>{paragraph}</p>
                  ))}
                </ContentText>
              )}
              {section.bullets && (
                <div
                  style={{
                    marginTop: section.paragraphs?.length || section.boldParagraphs?.length ? '16px' : 0,
                  }}
                >
                  {renderBulletCards(section.bullets, { grid: BulletCardsGridColumn })}
                </div>
              )}
              {section.paragraphsAfter && (
                <ContentText
                  style={{
                    marginTop: section.bullets ? '24px' : section.paragraphs?.length ? '16px' : 0,
                  }}
                >
                  {section.paragraphsAfter.map((paragraph, pIndex) => (
                    <p key={`p-after-${pIndex}`}>{paragraph}</p>
                  ))}
                </ContentText>
              )}
            </ImageTextContent>
          );
          return (
            <>
              <SolutionsSectionHeading style={{ marginBottom: 'clamp(20px, 3vw, 36px)' }}>
                {section.title}
              </SolutionsSectionHeading>
              <ImageTextSection style={{ marginTop: 0, marginBottom: 0 }}>
                {imageOnLeft ? (
                  <>
                    {imageBlock}
                    {contentBlock}
                  </>
                ) : (
                  <>
                    {contentBlock}
                    {imageBlock}
                  </>
                )}
              </ImageTextSection>
            </>
          );
        })()}
        {(!imageSrc || fullWidthSectionImage) && !titleThenImageText && section.title && (
          <SolutionsSectionHeading>{section.title}</SolutionsSectionHeading>
        )}
        {(!imageSrc || fullWidthSectionImage) && !titleThenImageText && section.boldParagraphs?.map((text, i) => (
          <SectionSubtitle key={`bold-${i}`}>{text}</SectionSubtitle>
        ))}
        {(!imageSrc || fullWidthSectionImage) && !titleThenImageText && section.paragraphs && (
          <ContentText>
            {section.paragraphs.map((paragraph, pIndex) => (
              <p key={`p-${pIndex}`}>{paragraph}</p>
            ))}
          </ContentText>
        )}
        {section.subsections?.map((subsec, subIndex) => (
          <SubsectionBlock
            key={`sub-${subIndex}`}
            subsec={subsec}
            layout={compactSubsections ? 'compact' : 'default'}
            renderBulletCards={renderBulletCards}
          />
        ))}
        {!titleThenImageText && section.bullets && renderBulletCards(section.bullets)}
        {!titleThenImageText && section.paragraphsAfter && (
          <ContentText>
            {section.paragraphsAfter.map((paragraph, pIndex) => (
              <p key={`p-after-${pIndex}`}>{paragraph}</p>
            ))}
          </ContentText>
        )}
      </ContentSectionWrapper>
    );
  }

  if (sectionType === 'cards') {
    const columns = section.columns || 3;
    return (
      <ContentSectionWrapper
        key={`section-${sectionIndex}`}
        as={motion.div}
        variants={staggerItem}
      >
        {section.title && <SolutionsSectionHeading>{section.title}</SolutionsSectionHeading>}
        {section.intro && (
          <ContentText style={{ marginBottom: '40px' }}>
            {section.intro.map((paragraph, pIndex) => (
              <p key={`p-${pIndex}`}>{paragraph}</p>
            ))}
          </ContentText>
        )}
        <GridContainer className={`${columns}-columns`}>
          {section.cards?.map((card, cardIndex) => (
            <FeatureCard
              key={`card-${cardIndex}`}
              as={motion.div}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: cardIndex * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              {card.title && <CardTitle>{card.title}</CardTitle>}
              {card.description && <CardDescription>{card.description}</CardDescription>}
              {card.bullets && (
                <div style={{ marginTop: '16px' }}>{renderBulletCards(card.bullets)}</div>
              )}
            </FeatureCard>
          ))}
        </GridContainer>
      </ContentSectionWrapper>
    );
  }

  if (sectionType === 'title-image-text') {
    if (!section.image) return null;
    return (
      <ContentSectionWrapper
        key={`section-${sectionIndex}`}
        as={motion.div}
        variants={staggerItem}
      >
        {section.title && <SolutionsSectionHeading>{section.title}</SolutionsSectionHeading>}
        <ImageTextSection style={{ marginTop: '24px' }}>
          <ImageTextImage as={motion.div} {...imageAnimation}>
            <LazyImage
              src={section.image}
              alt={section.title || 'Section visual'}
              height="100%"
              objectFit="cover"
              critical
            />
          </ImageTextImage>
          <ImageTextContent>
            {(section.contentTitle != null ? section.contentTitle : section.title) && (
              <SolutionsSectionHeading style={{ marginTop: 0, marginBottom: '12px' }}>
                {section.contentTitle != null ? section.contentTitle : section.title}
              </SolutionsSectionHeading>
            )}
            {section.boldParagraphs?.map((text, i) => (
              <SectionSubtitle key={`bold-${i}`}>{text}</SectionSubtitle>
            ))}
            {section.paragraphs && (
              <ContentText>
                {section.paragraphs.map((paragraph, pIndex) => (
                  <p key={`p-${pIndex}`}>{paragraph}</p>
                ))}
              </ContentText>
            )}
          </ImageTextContent>
        </ImageTextSection>
      </ContentSectionWrapper>
    );
  }

  if (sectionType === 'image-text') {
    if (section.image) {
      const pos = (section.imagePosition || 'left').toString().trim().toLowerCase();
      const imageOnLeft = pos !== 'right';
      const imageBlock = (
        <ImageTextImage as={motion.div} {...imageAnimation}>
          <LazyImage
            src={section.image}
            alt={section.title || 'Section visual'}
            height="100%"
            objectFit="cover"
            critical
          />
        </ImageTextImage>
      );
      const contentBlock = (
        <ImageTextContent>
          {section.title && <SolutionsSectionHeading>{section.title}</SolutionsSectionHeading>}
          {section.boldParagraphs?.map((text, i) => (
            <SectionSubtitle key={`bold-${i}`}>{text}</SectionSubtitle>
          ))}
          {section.paragraphs && (
            <ContentText style={{ marginBottom: '24px' }}>
              {section.paragraphs.map((paragraph, pIndex) => (
                <p key={`p-${pIndex}`}>{paragraph}</p>
              ))}
            </ContentText>
          )}
        </ImageTextContent>
      );
      return (
        <ContentSectionWrapper
          key={`section-${sectionIndex}`}
          as={motion.div}
          variants={staggerItem}
        >
          <ImageTextSection>
            {imageOnLeft ? (
              <>
                {imageBlock}
                {contentBlock}
              </>
            ) : (
              <>
                {contentBlock}
                {imageBlock}
              </>
            )}
          </ImageTextSection>
          {section.bullets && (
            <div style={{ marginTop: '32px' }}>{renderBulletCards(section.bullets)}</div>
          )}
          {section.paragraphsAfter && (
            <ContentText style={{ marginTop: '32px' }}>
              {section.paragraphsAfter.map((paragraph, pIndex) => (
                <p key={`p-after-${pIndex}`}>{paragraph}</p>
              ))}
            </ContentText>
          )}
        </ContentSectionWrapper>
      );
    }

    return (
      <ContentSectionWrapper
        key={`section-${sectionIndex}`}
        as={motion.div}
        variants={staggerItem}
      >
        {section.title && <SolutionsSectionHeading>{section.title}</SolutionsSectionHeading>}
        {section.boldParagraphs?.map((text, i) => (
          <SectionSubtitle key={`bold-${i}`}>{text}</SectionSubtitle>
        ))}
        {section.paragraphs && (
          <ContentText style={{ marginBottom: '24px' }}>
            {section.paragraphs.map((paragraph, pIndex) => (
              <p key={`p-${pIndex}`}>{paragraph}</p>
            ))}
          </ContentText>
        )}
        {section.bullets && (
          <div style={{ marginTop: '32px' }}>{renderBulletCards(section.bullets)}</div>
        )}
        {section.paragraphsAfter && (
          <ContentText style={{ marginTop: '32px' }}>
            {section.paragraphsAfter.map((paragraph, pIndex) => (
              <p key={`p-after-${pIndex}`}>{paragraph}</p>
            ))}
          </ContentText>
        )}
      </ContentSectionWrapper>
    );
  }

  return null;
}

const SubsectionPage = () => {
  useScrollToTop();
  const { solutions } = useContent();
  const { sectionId, subsectionId } = useParams();

  const section = solutions?.sections?.[sectionId];
  const subsection = section?.subsections?.[subsectionId];

  // Memoize sections to prevent dependency changes on every render
  const sections = useMemo(() => subsection?.sections || [], [subsection?.sections]);

  // Hero image: first from subsection.images or section
  const images = subsection?.images || [];
  const heroImage = images[0] || section?.heroImage;

  // Preload critical images (hero + all section/subsection images on the page)
  useEffect(() => {
    const criticalImages = [];
    if (heroImage) criticalImages.push(heroImage);
    (subsection?.sections || []).forEach((sec) => {
      const img = sec?.image || sec?.src;
      if (img) criticalImages.push(img);
      (sec?.subsections || []).forEach((subsec) => {
        if (subsec?.image) criticalImages.push(subsec.image);
      });
    });
    if (criticalImages.length > 0) preloadCriticalImages(criticalImages);
  }, [heroImage, subsection?.sections]);

  // Background preload: sibling subsections so next/prev navigation feels instant
  useEffect(() => {
    if (!section?.subsections) return;
    const ids = Object.keys(section.subsections);
    const idx = ids.indexOf(subsectionId);
    if (idx < 0) return;
    const half = Math.floor(BACKGROUND_PRELOAD_MAX_SIBLINGS / 2);
    const urls = [];
    for (let i = Math.max(0, idx - half); i <= Math.min(ids.length - 1, idx + half); i++) {
      if (i === idx) continue;
      const sub = section.subsections[ids[i]];
      urls.push(...getSubsectionImageUrls(sub, section));
    }
    if (urls.length > 0) preloadImagesInBackground(urls);
  }, [section, subsectionId]);

  const renderBulletCards = useCallback((bullets, options = {}) => {
    const GridComponent = options.grid || BulletCardsGrid;
    return (
      <GridComponent
        as={motion.div}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        {bullets.map((bullet, bIndex) => {
          const { title, text, items } = normalizeBullet(bullet);
          return (
            <BulletPointCard key={`bullet-${bIndex}`}>
              {title && <BulletPointCardTitle>{title}</BulletPointCardTitle>}
              {items && items.length > 0 ? (
                <BulletPointCardList>
                  {items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </BulletPointCardList>
              ) : null}
              {!items?.length && text ? <BulletPointCardText>{text}</BulletPointCardText> : null}
            </BulletPointCard>
          );
        })}
      </GridComponent>
    );
  }, []);

  if (!subsection) {
    return (
      <PageContainer>
        <Container>
          <PageTitle
            className="with-underline"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            Subsection Not Found
          </PageTitle>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={{
              fontSize: '18px',
              color: 'rgba(29, 51, 78, 0.8)',
              textAlign: 'center'
            }}
          >
            The requested subsection could not be found.
          </motion.p>
        </Container>
      </PageContainer>
    );
  }

  const content = subsection.content || [];
  const cta = subsection.cta;

  return (
    <>
      <SEO
        title={`${subsection.title} – APEXGATE GLOBAL`}
        description={subsection.description || ''}
        image={heroImage}
      />
      {heroImage ? (
        <LazyBackgroundImage
          src={heroImage}
          critical={true}
          rounded={false}
          backgroundPosition="50% 70%"
        >
          <SolutionsHeroSection>
            <Container>
              <HeroContentWrapper>
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={heroStaggerContainerVariants}
                >
                  <HeroContent style={{ animation: 'none' }}>
                    <HeroTitle as={motion.h1} variants={heroStaggerItemVariants}>
                      {subsection.title}
                    </HeroTitle>
                    {subsection.description != null && String(subsection.description).trim() !== '' && (
                      <HeroSubtitle as={motion.p} variants={heroStaggerItemVariants}>
                        {subsection.description}
                      </HeroSubtitle>
                    )}
                  </HeroContent>
                </motion.div>
              </HeroContentWrapper>
            </Container>
          </SolutionsHeroSection>
        </LazyBackgroundImage>
      ) : null}

      <PageContainer style={heroImage ? { paddingTop: '60px' } : undefined}>
        <Container>
          {!heroImage && (
            <Section>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={heroStaggerContainerVariants}
              >
                <PageTitle
                  className="with-underline"
                  variants={heroStaggerItemVariants}
                >
                  {subsection.title}
                </PageTitle>
                {subsection.description != null && String(subsection.description).trim() !== '' && (
                  <PageSubtitle variants={heroStaggerItemVariants}>
                    {subsection.description}
                  </PageSubtitle>
                )}
              </motion.div>
            </Section>
          )}

          {/* Simple content paragraphs (backward compatibility) */}
          {content.length > 0 && sections.length === 0 && (
            <Section>
              <ContentText>
                {content.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </ContentText>
            </Section>
          )}

          {/* Structured sections */}
          {sections.length > 0 && (
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {sections.map((section, sectionIndex) =>
                renderSectionContent(section, sectionIndex, { renderBulletCards })
              )}
            </motion.div>
          )}
        </Container>

        {/* CTA block: image + title + text + button (same as SectionPage) */}
        {(cta?.title || cta?.text || cta?.buttonText) && (
          <Container>
            <CtaImageSection src={cta?.backgroundImage || heroImage || '/ui/handshake-loop.webp'} overlay={0.45}>
              <PartnershipSection
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
              >
                <PartnershipContent>
                  {cta?.title != null && cta.title.trim() !== '' && (
                    <SolutionsCtaTitle as={motion.h3} variants={staggerItem}>
                      {cta.title.toUpperCase()}
                    </SolutionsCtaTitle>
                  )}
                  {cta?.text != null && cta.text.trim() !== '' && (
                    <SolutionsCtaText as={motion.p} variants={staggerItem}>
                      {cta.text}
                    </SolutionsCtaText>
                  )}
                  {cta?.buttonText != null && (
                    <SolutionsCtaButton to={cta?.buttonLink || '/contact'}>
                      {cta.buttonText}
                    </SolutionsCtaButton>
                  )}
                </PartnershipContent>
              </PartnershipSection>
            </CtaImageSection>
          </Container>
        )}
      </PageContainer>
    </>
  );
};

export default SubsectionPage;

